import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';

/**
 * NotFoundPage — clean, branded 404.
 * Replaces the previous silent "redirect to /" behavior on unknown routes,
 * which felt blank / search-like to users hitting mistyped URLs.
 *
 * Keeps consistent with BITZ brand (gold + obsidian) and offers two clear
 * actions: go home, or go back. No staff URLs are exposed.
 */
const NotFoundPage = () => {
  const location = useLocation();

  return (
    <div
      className="min-h-screen bg-[#0F0F10] flex items-center justify-center px-4"
      data-testid="not-found-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1750763533241-7c2beccb9eab?crop=entropy&cs=srgb&fm=jpg&q=70)",
        }}
      />
      <div className="relative z-10 w-full max-w-lg text-center">
        <Link to="/" className="inline-flex flex-col items-center gap-2 mb-8">
          <img
            src="/brand/bci-logo-512.png"
            alt="BCI"
            className="w-16 h-16 object-contain drop-shadow-[0_8px_24px_rgba(212,175,55,0.35)]"
          />
        </Link>

        <p
          className="text-[80px] md:text-[120px] leading-none font-bold text-transparent bg-clip-text bg-gradient-to-br from-[#D4AF37] via-[#F5E6A3] to-[#8B6914]"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          404
        </p>

        <h1
          className="text-2xl md:text-3xl font-bold text-white mt-2 mb-3"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Page Not Found
        </h1>

        <p className="text-gray-400 text-sm md:text-base mb-2">
          The page you&apos;re looking for doesn&apos;t exist or has moved.
        </p>
        {location.pathname && (
          <p
            className="text-xs text-gray-500 font-mono mb-8 break-all px-4"
            data-testid="not-found-attempted-path"
          >
            {location.pathname}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link
            to="/"
            className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="not-found-home-btn"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="not-found-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>

        <p className="text-[11px] text-gray-600 mt-10">
          BITZ Club International · Premium Worldwide Privileges
        </p>
      </div>
    </div>
  );
};

export default NotFoundPage;
